import { type RefObject } from "react";
import { styled } from "@pigment-css/react";
import type { Meeting } from "@acto/taskpane/core/domain";
import { NameText } from "./ui/NameText";

// Blank glass box that does the visible travel/resize. Content is NOT inside it —
// it lives in the two fixed overlays below, pinned to the real endpoint rects.
const Flyer = styled("div")(({ theme }) => ({
  position: "fixed",
  zIndex: 60,
  opacity: 0,
  pointerEvents: "none",
  boxSizing: "border-box",
  overflow: "hidden",
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
}));

// Card content, sized to the card endpoint and anchored top-left inside the flyer.
// Fixed size → never reflows; the flyer's overflow:hidden clips it; it rides the box.
const FlyerCardLayer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  opacity: 0,
  overflow: "hidden",
  display: "flex",
  alignItems: "flex-start",
  flexShrink: 0,
  gap: theme.fieldSalesTwo.spacing[16],
  padding: theme.fieldSalesTwo.spacing[24],
  boxSizing: "border-box",
}));

// Must stay byte-identical to CustomerCard's DateBadge (incl. the date/time spans): the
// flyer replaces the real card in the same frame at the morph handoff, so any style
// drift shows as a text jump.
const FlyerBadge = styled("div")(({ theme }) => ({
  flexShrink: 0,
  width: 56,
  height: 60,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  borderRadius: theme.fieldSalesTwo.radius[18],
  background: "rgba(19, 19, 19, 0.04)",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  lineHeight: 1.32,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  textAlign: "center",
}));

const FlyerBadgeDate = styled("span")(({ theme }) => ({
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  color: "rgba(19, 19, 19, 0.9)",
}));

const FlyerBadgeTime = styled("span")(({ theme }) => ({
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const FlyerCardText = styled("div")(({ theme }) => ({
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  paddingTop: theme.fieldSalesTwo.spacing[4],
}));

const FlyerCardTitle = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: 1.36,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const FlyerCardRef = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: "rgba(19, 19, 19, 0.72)",
}));

// Pill content (name), sized to the pill endpoint and anchored top-left inside the
// flyer. Fixed size → never reflows; clipped by the flyer; rides the box.
const FlyerPillLayer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  opacity: 0,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  overflow: "hidden",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  // Mirrors NamePill (TitleBar) so the label colour doesn't pop at the morph handoff.
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

interface MorphFlyerProps {
  flyerRef: RefObject<HTMLDivElement | null>;
  cardLayerRef: RefObject<HTMLDivElement | null>;
  pillLayerRef: RefObject<HTMLDivElement | null>;
  meeting: Meeting;
  /** Suppress the date badge (ad-hoc recap has no meeting date). */
  hideDate?: boolean;
  /** Mirror of CustomerCard's showBadgeTime — must match the real card's badge shape. */
  showBadgeTime?: boolean;
  name: string;
}

export function MorphFlyer({
  flyerRef,
  cardLayerRef,
  pillLayerRef,
  meeting,
  hideDate,
  showBadgeTime,
  name,
}: MorphFlyerProps) {
  return (
    <Flyer ref={flyerRef} aria-hidden="true">
      <FlyerCardLayer ref={cardLayerRef}>
        {!hideDate && (
          <FlyerBadge>
            <FlyerBadgeDate>{meeting.scheduledDate.format("DD.MM")}</FlyerBadgeDate>
            {showBadgeTime && meeting.scheduledStartTime && (
              <FlyerBadgeTime>{meeting.scheduledStartTime.slice(0, 5)}</FlyerBadgeTime>
            )}
          </FlyerBadge>
        )}
        <FlyerCardText>
          <FlyerCardTitle>{name}</FlyerCardTitle>
          <FlyerCardRef>{meeting.customer.externalReference}</FlyerCardRef>
        </FlyerCardText>
      </FlyerCardLayer>
      <FlyerPillLayer ref={pillLayerRef}>
        <NameText>{name}</NameText>
      </FlyerPillLayer>
    </Flyer>
  );
}
